"use client";

import Link from "next/link";
import { Eye, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { editorHref } from "@/utils/editor-href";

interface FunnelResultToastProps {
  title: string;
  subtitle: string;
  funnelId: number;
  name: string;
}

const FunnelResultToastBody = ({
  subtitle,
  funnelId,
  name,
}: Omit<FunnelResultToastProps, "title">) => (
  <div className="flex flex-col gap-2 text-muted-foreground">
    <span>{subtitle}</span>
    <div className="flex items-center justify-between gap-2">
      <span className="font-semibold text-primary">
        #{funnelId} {name}
      </span>
      <div className="flex items-center gap-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7 p-0"
                asChild
                onClick={(e) => e.stopPropagation()}
              >
                <Link href={editorHref({ kind: "funnel", id: funnelId })}>
                  <Pencil className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Edit</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className="h-7 w-7 p-0"
                asChild
                onClick={(e) => e.stopPropagation()}
              >
                <Link
                  href={`/funnels/${funnelId}/overview`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Eye className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Preview</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  </div>
);

export const toastFunnelResult = ({
  title,
  subtitle,
  funnelId,
  name,
}: FunnelResultToastProps) => {
  toast.success(title, {
    description: (
      <FunnelResultToastBody
        subtitle={subtitle}
        funnelId={funnelId}
        name={name}
      />
    ),
    duration: 10000,
    classNames: {
      icon: "self-start mt-0.5",
      content: "flex flex-col w-full",
      description: "flex flex-col gap-2",
    },
  });
};
